import { useEffect, useState } from "react";
import pLimit from "p-limit";
import {
  composerImageSrc,
  type ComposerFile,
} from "src/components/llm/utils/file-processing";
import { captureException } from "src/lib/monitoring";
import { compressImageBlob } from "src/utils/compress-image";

// Cache small encoded previews without retaining removed attachment objects or decoded images.
const thumbnails = new WeakMap<ComposerFile, Blob>();
const generateThumbnail = pLimit(1);

export const useComposerThumbnail = (
  file: ComposerFile | undefined,
  isVisible: boolean,
) => {
  const [preview, setPreview] = useState<{ file: ComposerFile; url: string }>();

  useEffect(() => {
    if (!file || !isVisible) return;
    const source = composerImageSrc(file);
    if (!source) return;
    let cancelled = false;
    let url: string | undefined;

    const load = async () => {
      try {
        const thumbnail = await generateThumbnail(async () => {
          if (cancelled) return;
          const cached = thumbnails.get(file);
          if (cached) return cached;
          let original: Blob | undefined = file.file;
          if (!original) {
            const response = await fetch(source);
            if (!response.ok)
              throw new Error("Could not read attachment preview");
            original = await response.blob();
          }
          if (cancelled) return;
          const resized = await compressImageBlob(original, {
            maxDimension: 480,
            quality: 0.75,
          });
          // The compressor returns the original when canvas encoding is unavailable.
          if (resized === original) return;
          thumbnails.set(file, resized);
          return resized;
        });
        if (cancelled || !thumbnail) return;
        url = URL.createObjectURL(thumbnail);
        setPreview({ file, url });
      } catch (error) {
        captureException(error);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
      setPreview(undefined);
    };
  }, [file, isVisible]);

  return isVisible && preview?.file === file ? preview?.url : undefined;
};
